import {
  Cartesian3,
  Matrix4,
  Ray,
  Quaternion,
  Matrix3,
  Cartesian2,
  Scene,
  Plane,
  Color,
  defined,
  IntersectionTests,
  destroyObject,
} from "cesium";
import AxisLinePrimitive from "components/elements/CesiumViewer/AxisLinePrimitive";
import getWidgetOrigin from "components/elements/CesiumViewer/getWidgetOrigin";
import TransformAxis from "components/elements/CesiumViewer/TransformAxis";
import { Math as CesiumMath } from "cesium";
import { RotationUtils } from "./core/utils/RotationUtils";

const noScale = new Cartesian3(1.0, 1.0, 1.0);
const offsetScratch = new Cartesian3();
const widgetOriginScratch = new Cartesian3();
const rotationWorldScratch = new Cartesian3();
const vector1Scratch = new Cartesian3();
const vector2Scratch = new Cartesian3();
const rayScratch = new Ray();
const intersectionScratch = new Cartesian3();
const matrix3Scratch = new Matrix3();
const modelMatrixScratch = new Matrix4();
const newModelOriginScratch = new Cartesian3();
const quaternionScratch = new Quaternion();

interface CirclePositions {
  x: Cartesian3[];
  y: Cartesian3[];
  z: Cartesian3[];
}

function getUnitCirclePositions(): CirclePositions {
  const xAxis: Cartesian3[] = [];
  const yAxis: Cartesian3[] = [];
  const zAxis: Cartesian3[] = [];

  for (let i = 0; i < 360; i++) {
    const rad = CesiumMath.toRadians(i);
    const x = Math.cos(rad);
    const y = Math.sin(rad);

    xAxis.push(new Cartesian3(0.0, x, y));
    yAxis.push(new Cartesian3(y, 0.0, x));
    zAxis.push(new Cartesian3(x, y, 0.0));
  }
  return {
    x: xAxis,
    y: yAxis,
    z: zAxis,
  };
}

function getLinePrimitive(
  positions: Cartesian3[],
  axis: string,
): AxisLinePrimitive {
  return new AxisLinePrimitive({
    positions: positions,
    color: TransformAxis.getColor(axis),
    loop: true,
    show: false,
    id: axis,
  });
}

/**
 * Options for constructing a RotationEditor
 */
interface RotationEditorOptions {
  /** The scene where the editor will be rendered */
  scene: Scene;
  /** The offset from the model origin to display the widget */
  originOffset: Cartesian3;
  /** Callback function to set the heading pitch roll when rotating */
  setRotationQuaternion: (quaternion: Quaternion) => void;
  /** Callback function to set the position when rotating */
  setPosition: (position: Cartesian3) => void;
  /** The transformation matrix of the object being edited */
  localToWorldTransform: Matrix4;
  /** The radius of the object */
  radius: number;
}

/**
 * A widget for rotating objects around X, Y, and Z axes in 3D space
 */
class RotationEditor {
  private _vectorLine1: AxisLinePrimitive;
  private _vectorLine2: AxisLinePrimitive;
  private _polylineX: AxisLinePrimitive;
  private _polylineY: AxisLinePrimitive;
  private _polylineZ: AxisLinePrimitive;
  private _scene: Scene;
  private _setRotationQuaternionCallback: (quaternion: Quaternion) => void;
  private _setPositionCallback: (position: Cartesian3) => void;
  private _localToWorldTransform: Matrix4;
  private _radius: number;
  private _active: boolean;
  private _dragging: boolean;
  private _startTransform: Matrix4;
  private _startRotation: Matrix3;
  private _widgetOrigin: Cartesian3;
  private _modelOrigin: Cartesian3;
  private _rotationPlane: Plane;
  private _rotationStartPoint: Cartesian3;
  private _selectedAxis: Cartesian3;

  originOffset: Cartesian3;

  constructor(options: RotationEditorOptions) {
    const scene = options.scene;

    this._vectorLine1 = scene.primitives.add(
      new AxisLinePrimitive({
        width: 5,
        positions: [new Cartesian3(), new Cartesian3()],
        color: Color.YELLOW,
        show: false,
      }),
    );
    this._vectorLine2 = scene.primitives.add(
      new AxisLinePrimitive({
        width: 5,
        positions: [new Cartesian3(), new Cartesian3()],
        color: Color.YELLOW,
        show: false,
      }),
    );

    const circles = getUnitCirclePositions();

    this._polylineX = scene.primitives.add(
      getLinePrimitive(circles.x, TransformAxis.X),
    );
    this._polylineY = scene.primitives.add(
      getLinePrimitive(circles.y, TransformAxis.Y),
    );
    this._polylineZ = scene.primitives.add(
      getLinePrimitive(circles.z, TransformAxis.Z),
    );

    this.originOffset = options.originOffset;
    this._scene = scene;
    this._setRotationQuaternionCallback = options.setRotationQuaternion;
    this._setPositionCallback = options.setPosition;
    this._localToWorldTransform = options.localToWorldTransform;
    this._radius = options.radius;

    this._active = false;
    this._dragging = false;
    this._startTransform = new Matrix4();
    this._startRotation = new Matrix3();
    this._widgetOrigin = new Cartesian3();
    this._modelOrigin = new Cartesian3();
    this._rotationPlane = new Plane(Cartesian3.UNIT_X, 0.0);
    this._rotationStartPoint = new Cartesian3();
    this._selectedAxis = new Cartesian3();

    this.update();
  }

  get active(): boolean {
    return this._active;
  }

  set active(active: boolean) {
    this._active = active;
    if (active) {
      this._polylineX.show = true;
      this._polylineY.show = true;
      this._polylineZ.show = true;
    } else {
      this._polylineX.show = false;
      this._polylineY.show = false;
      this._polylineZ.show = false;
      this._dragging = false;
    }
  }

  get localToWorldTransform(): Matrix4 {
    return this._localToWorldTransform;
  }

  set localToWorldTransform(value: Matrix4) {
    this._localToWorldTransform = value;
    this.update();
  }

  update(): void {
    const localToWorldTransform = this._localToWorldTransform;
    let modelMatrix = Matrix4.clone(localToWorldTransform, modelMatrixScratch);

    const widgetOrigin = getWidgetOrigin(
      localToWorldTransform,
      this.originOffset,
      widgetOriginScratch,
    );
    modelMatrix = Matrix4.setTranslation(
      modelMatrix,
      widgetOrigin,
      modelMatrix,
    );

    const radius =
      this._radius *
      Matrix4.getMaximumScale(this._localToWorldTransform) *
      1.25;
    modelMatrix = Matrix4.multiplyByUniformScale(
      modelMatrix,
      radius,
      modelMatrix,
    );

    this._polylineX.modelMatrix = modelMatrix;
    this._polylineY.modelMatrix = modelMatrix;
    this._polylineZ.modelMatrix = modelMatrix;
  }

  handleLeftDown(position: Cartesian2): void {
    const scene = this._scene;
    const pickedObjects = scene.drillPick(position);
    let pickedAxis: string | undefined;
    for (let i = 0; i < pickedObjects.length; i++) {
      const object = pickedObjects[i];
      if (defined(object.id) && defined(TransformAxis[object.id])) {
        pickedAxis = object.id;
        break;
      }
    }
    if (!defined(pickedAxis)) {
      return;
    }

    const rotationAxis = TransformAxis.getValue(pickedAxis);
    this._selectedAxis = Cartesian3.clone(rotationAxis, this._selectedAxis);

    const startTransform = Matrix4.setScale(
      this._localToWorldTransform,
      noScale,
      this._startTransform,
    );
    this._startRotation = Matrix4.getMatrix3(
      startTransform,
      this._startRotation,
    );
    Matrix4.getTranslation(startTransform, this._modelOrigin);

    const widgetOrigin = getWidgetOrigin(
      this._localToWorldTransform,
      this.originOffset,
      this._widgetOrigin,
    );

    // Get the rotation axis direction in world space
    const rotationAxisVectorWorld = RotationUtils.getRotationAxisInWorldSpace(
      startTransform,
      rotationAxis,
      rotationWorldScratch,
    );

    // Plane that passes through widget origin and is perpendicular to the direction vector
    const rotationPlane = Plane.fromPointNormal(
      widgetOrigin,
      rotationAxisVectorWorld,
      this._rotationPlane,
    );
    const rotationStartPoint = IntersectionTests.rayPlane(
      scene.camera.getPickRay(position, rayScratch),
      rotationPlane,
      this._rotationStartPoint,
    );
    this._dragging = defined(rotationStartPoint);
    scene.screenSpaceCameraController.enableInputs = false;
  }

  handleMouseMove(position: Cartesian2): void {
    if (!this._dragging) {
      return;
    }
    const scene = this._scene;
    const ray = scene.camera.getPickRay(position, rayScratch);
    let intersection = IntersectionTests.rayPlane(
      ray,
      this._rotationPlane,
      intersectionScratch,
    );

    if (!defined(intersection)) {
      return;
    }

    const rotationStartPoint = this._rotationStartPoint;
    const vector1 = this._vectorLine1;
    const v1Pos = vector1.positions;
    const vector2 = this._vectorLine2;
    const v2Pos = vector2.positions;

    let v1 = Cartesian3.subtract(
      rotationStartPoint,
      this._widgetOrigin,
      vector1Scratch,
    );
    let v2 = Cartesian3.subtract(
      intersection,
      this._widgetOrigin,
      vector2Scratch,
    );
    v2 = Cartesian3.normalize(v2, v2);
    v2 = Cartesian3.multiplyByScalar(v2, Cartesian3.magnitude(v1), v2);
    intersection = Cartesian3.add(this._widgetOrigin, v2, intersection);

    v1Pos[0] = this._widgetOrigin;
    v1Pos[1] = rotationStartPoint;
    v2Pos[0] = this._widgetOrigin;
    v2Pos[1] = intersection;
    vector1.positions = v1Pos;
    vector2.positions = v2Pos;
    vector1.show = true;
    vector2.show = true;

    Cartesian3.normalize(v1, v1);
    Cartesian3.normalize(v2, v2);

    const rotationAxisWorld = RotationUtils.getRotationAxisInWorldSpace(
      this._startTransform,
      this._selectedAxis,
      rotationWorldScratch,
    );

    let quaternion = RotationUtils.quaternionFromVectors(
      v1,
      v2,
      rotationAxisWorld,
      quaternionScratch,
    );

    const offset = Cartesian3.multiplyComponents(
      this.originOffset,
      Matrix4.getScale(this._localToWorldTransform, offsetScratch),
      offsetScratch,
    );
    let rotation = Matrix3.fromQuaternion(quaternion, matrix3Scratch);

    this._setRotationQuaternionCallback(quaternion);

    const newModelOrigin = RotationUtils.calculateRotatedModelOrigin(
      this._widgetOrigin,
      offset,
      rotation,
      newModelOriginScratch,
    );
    this._setPositionCallback(newModelOrigin);
  }

  handleLeftUp(): void {
    this._dragging = false;
    this._vectorLine1.show = false;
    this._vectorLine2.show = false;
    this._selectedAxis = Cartesian3.clone(Cartesian3.ZERO, this._selectedAxis);
    this._scene.screenSpaceCameraController.enableInputs = true;
  }

  destroy(): void {
    this.active = false;
    const scene = this._scene;

    scene?.primitives?.remove(this._vectorLine2);
    scene?.primitives?.remove(this._vectorLine1);
    scene?.primitives?.remove(this._polylineX);
    scene?.primitives?.remove(this._polylineY);
    scene?.primitives?.remove(this._polylineZ);

    destroyObject(this);
  }
}

export default RotationEditor;
